import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import serviceWorkerManager from '../services/serviceWorkerManager';
import pageVisibilityManager from '../services/pageVisibilityManager';
import { isBackgroundAnalysisSupported, analyzeWebsitesEnhanced } from '../services/websiteAnalyzer';

const ServiceWorkerTest = () => {
  const [serviceWorkerStatus, setServiceWorkerStatus] = useState('checking');
  const [backgroundSupported, setBackgroundSupported] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [isRunningTest, setIsRunningTest] = useState(false);
  const [pageVisibility, setPageVisibility] = useState(pageVisibilityManager.isPageVisible());
  const [activeTasks, setActiveTasks] = useState([]);

  useEffect(() => {
    // Check service worker status
    const checkStatus = async () => {
      const isAvailable = serviceWorkerManager.isAvailable();
      const backgroundSupport = isBackgroundAnalysisSupported();
      
      setServiceWorkerStatus(isAvailable ? 'available' : 'unavailable');
      setBackgroundSupported(backgroundSupport);
    };

    checkStatus();

    // Listen for page visibility changes
    const unsubscribe = pageVisibilityManager.addVisibilityChangeHandler((isVisible) => {
      setPageVisibility(isVisible);
    });

    // Update active tasks periodically
    const taskInterval = setInterval(() => {
      const tasks = pageVisibilityManager.getActiveBackgroundTasks();
      setActiveTasks(tasks);
    }, 1000);

    return () => {
      unsubscribe();
      clearInterval(taskInterval);
    };
  }, []);

  const runServiceWorkerTest = async () => {
    setIsRunningTest(true);
    setTestResults([]);

    const addResult = (test, status, message) => {
      setTestResults(prev => [...prev, { test, status, message, timestamp: new Date().toLocaleTimeString() }]);
    };

    try {
      // Test 1: Service Worker Registration
      addResult('Service Worker Registration', 'running', 'Checking service worker registration...');
      const isRegistered = serviceWorkerManager.isAvailable();
      addResult('Service Worker Registration', isRegistered ? 'success' : 'failed', 
        isRegistered ? 'Service worker is registered and active' : 'Service worker is not available');

      if (!isRegistered) {
        addResult('Overall Test', 'failed', 'Cannot proceed without service worker');
        setIsRunningTest(false);
        return;
      }

      // Test 2: Background Analysis Support
      addResult('Background Analysis Support', 'running', 'Checking background analysis capabilities...');
      const backgroundSupport = isBackgroundAnalysisSupported();
      addResult('Background Analysis Support', backgroundSupport ? 'success' : 'failed',
        backgroundSupport ? 'Background analysis is supported' : 'Background analysis not supported');

      // Test 3: Page Visibility API
      addResult('Page Visibility API', 'running', 'Testing page visibility detection...');
      const isVisible = pageVisibilityManager.isPageVisible();
      addResult('Page Visibility API', 'success', `Page is currently ${isVisible ? 'visible' : 'hidden'}`);

      // Test 4: Mock Website Analysis
      addResult('Website Analysis Test', 'running', 'Starting mock website analysis...');
      
      const mockWebsites = [
        { url: 'https://example.com', company: 'Example Corp' },
        { url: 'https://google.com', company: 'Google' }
      ];
      
      const mockConfig = {
        endpoint: 'openai',
        apiKey: 'test-key',
        model: 'gpt-3.5-turbo'
      };
      
      const mockServiceOptions = {
        serviceType: 'website'
      };
      
      const mockScrapingOptions = {
        scrapeWebsite: false, // Disable actual scraping for test
        scrapeLinkedIn: false,
        scrapeTalkingPoints: false
      };

      let progressCount = 0;
      const onProgress = (current, result, finalResults) => {
        progressCount++;
        if (finalResults) {
          addResult('Website Analysis Test', 'success', `Analysis completed with ${finalResults.length} results`);
        } else {
          addResult('Website Analysis Test', 'running', `Progress: ${current}/${mockWebsites.length} websites`);
        }
      };

      try {
        // This will test the service worker fallback since we don't have a real API key
        const results = await analyzeWebsitesEnhanced(
          mockWebsites,
          mockConfig,
          onProgress,
          mockServiceOptions,
          mockScrapingOptions,
          true // Use service worker
        );
        
        addResult('Website Analysis Test', 'success', `Mock analysis completed with ${results.length} results`);
      } catch (error) {
        addResult('Website Analysis Test', 'warning', `Analysis failed as expected (no real API key): ${error.message}`);
      }

      // Test 5: Task Management
      addResult('Task Management', 'running', 'Testing background task registration...');
      const taskId = serviceWorkerManager.generateTaskId();
      pageVisibilityManager.registerBackgroundTask(taskId, 'test_task', { test: true });
      
      const tasks = pageVisibilityManager.getActiveBackgroundTasks();
      const testTask = tasks.find(task => task.taskId === taskId);
      
      if (testTask) {
        addResult('Task Management', 'success', 'Background task registered successfully');
        pageVisibilityManager.unregisterBackgroundTask(taskId);
        addResult('Task Management', 'success', 'Background task unregistered successfully');
      } else {
        addResult('Task Management', 'failed', 'Failed to register background task');
      }

      addResult('Overall Test', 'success', 'All service worker tests completed successfully!');
      
    } catch (error) {
      addResult('Overall Test', 'failed', `Test suite failed: ${error.message}`);
    }

    setIsRunningTest(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return 'CheckCircle';
      case 'failed': return 'XCircle';
      case 'warning': return 'AlertTriangle';
      case 'running': return 'Clock';
      default: return 'Info';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'failed': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'running': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg"
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Service Worker Test Dashboard</h2>
        <p className="text-gray-600">Test and monitor background processing capabilities</p>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2">
            <SafeIcon 
              name={serviceWorkerStatus === 'available' ? 'CheckCircle' : 'XCircle'} 
              className={`w-5 h-5 ${serviceWorkerStatus === 'available' ? 'text-green-600' : 'text-red-600'}`} 
            />
            <span className="font-medium">Service Worker</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {serviceWorkerStatus === 'available' ? 'Active' : 'Unavailable'}
          </p>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2">
            <SafeIcon 
              name={backgroundSupported ? 'CheckCircle' : 'XCircle'} 
              className={`w-5 h-5 ${backgroundSupported ? 'text-green-600' : 'text-red-600'}`} 
            />
            <span className="font-medium">Background Analysis</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {backgroundSupported ? 'Supported' : 'Not Supported'}
          </p>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2">
            <SafeIcon 
              name={pageVisibility ? 'Eye' : 'EyeOff'} 
              className={`w-5 h-5 ${pageVisibility ? 'text-green-600' : 'text-yellow-600'}`} 
            />
            <span className="font-medium">Page Visibility</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {pageVisibility ? 'Visible' : 'Hidden'}
          </p>
        </div>
      </div>

      {/* Active Tasks */}
      {activeTasks.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Active Background Tasks</h3>
          <div className="space-y-2">
            {activeTasks.map((task) => (
              <div key={task.taskId} className="bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-blue-900">{task.type}</span>
                  <span className="text-sm text-blue-600">{task.status}</span>
                </div>
                <p className="text-sm text-blue-700 mt-1">Task ID: {task.taskId}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Test Controls */}
      <div className="mb-6">
        <button
          onClick={runServiceWorkerTest}
          disabled={isRunningTest}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors"
        >
          {isRunningTest ? 'Running Tests...' : 'Run Service Worker Tests'}
        </button>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Test Results</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {testResults.map((result, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
              >
                <SafeIcon 
                  name={getStatusIcon(result.status)} 
                  className={`w-5 h-5 mt-0.5 ${getStatusColor(result.status)}`} 
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{result.test}</span>
                    <span className="text-sm text-gray-500">{result.timestamp}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{result.message}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ServiceWorkerTest;